import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { ArrowRight } from "@/icons";
import { EASE } from "@/animations/motion";
import type { WorkflowNode } from "@/types/detail";

/** Interactive workflow timeline — click a step to reveal its detail. */
export function WorkflowTimeline({
  nodes,
  accent = "#0F766E",
}: {
  nodes: WorkflowNode[];
  accent?: string;
}) {
  const [active, setActive] = useState(0);

  return (
    <div>
      <ol className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-0">
        {nodes.map((node, i) => (
          <li key={node.label} className="flex flex-1 items-center gap-3 lg:flex-col lg:gap-0">
            <button
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={active === i}
              aria-controls="workflow-detail-panel"
              data-testid={`workflow-node-${i}`}
              className={cn(
                "flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all duration-300 lg:flex-col lg:items-center lg:gap-2 lg:text-center",
                active === i
                  ? "border-transparent text-white shadow-[0_16px_40px_-18px_rgba(15,118,110,0.5)]"
                  : "border-border bg-card text-foreground hover:border-primary/30",
              )}
              style={active === i ? { background: accent } : undefined}
            >
              <span
                className={cn(
                  "inline-flex h-8 w-8 items-center justify-center rounded-full font-mono text-sm font-semibold",
                  active === i ? "bg-white/20 text-white" : "bg-muted text-muted-foreground",
                )}
              >
                {i + 1}
              </span>
              <span className="font-heading text-sm font-semibold">{node.label}</span>
            </button>
            {i < nodes.length - 1 && (
              <ArrowRight className="hidden h-4 w-4 shrink-0 rotate-90 text-muted-foreground lg:block lg:rotate-0" />
            )}
          </li>
        ))}
      </ol>

      <div
        id="workflow-detail-panel"
        aria-live="polite"
        className="mt-6 rounded-2xl border border-border bg-card p-6"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: EASE }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>
              Step {active + 1} · {nodes[active].label}
            </p>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              {nodes[active].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
